import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { Brand } from "@/components/Brand";
import { DisclosureFooter } from "@/components/FundingDisclosure";
import { FundingReadyBadge } from "@/components/funding/FundingReadyBadge";
import { TierBadge } from "@/components/funding/TierBadge";
import { usePageMeta } from "@/lib/seo";
import { api } from "@/lib/api";

function Docs({ docs }) {
  if (!docs.length) return <p className="text-sm text-slate-500" data-testid="public-ready-no-docs">No documents verified yet.</p>;
  return (
    <ul className="divide-y divide-slate-100 border border-slate-200 rounded-lg bg-white" data-testid="public-ready-docs">
      {docs.map((d, i) => <li key={`${d.name}-${i}`} className="flex items-center gap-3 px-4 py-3 text-sm"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /><span className="flex-1 text-slate-800">{d.name}</span>{d.tier && <TierBadge tier={d.tier} />}</li>)}
    </ul>
  );
}

export default function FundingReadyPublic() {
  const { shareId } = useParams();
  const [params] = useSearchParams();
  const preview = params.get("preview") === "1";
  const [d, setD] = useState(null);
  const [missing, setMissing] = useState(false);
  const fetched = useRef(false);
  useEffect(() => {
    if (fetched.current) return;
    fetched.current = true;
    api.get(`/public/funding-ready/${shareId}`, { params: preview ? { preview: true } : {} }).then((r) => setD(r.data)).catch(() => setMissing(true));
  }, [shareId, preview]);
  usePageMeta({ title: d ? `${d.business_name} · Funding Ready status | GrantVerify` : "Funding Ready status | GrantVerify", path: `/funding-ready/${shareId}`,
    description: "Verified compliance readiness for banks, lenders and sponsors, powered by GrantVerify." });
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="public-ready-page">
      <header className="border-b border-slate-200 bg-white"><div className="max-w-3xl mx-auto px-4 sm:px-6 py-5"><Brand /></div></header>
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-14">
        {missing && <p className="text-slate-600" data-testid="public-ready-missing">This Funding Ready link is turned off or no longer exists.</p>}
        {d && (
          <>
            <div className="eyebrow mb-3">Compliance readiness report</div>
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900" data-testid="public-ready-name">{d.business_name}</h1>
            <p className="mt-2 text-slate-600">{d.industry} · {d.city ? `${d.city}, ` : ""}{d.state}</p>
            <div className="mt-8 bg-slate-900 text-white rounded-lg p-6 flex flex-wrap items-center gap-6">
              <div><div className="text-5xl font-bold" data-testid="public-ready-pct">{d.pct}%</div><div className="text-xs text-slate-400">requirements met</div></div>
              <div className="text-sm"><b>{d.met}</b> of {d.total} met · <b>{d.verified}</b> verified with proof</div>
              <div className="ml-auto">{d.funding_ready ? <FundingReadyBadge ready testid="public-ready-badge" /> : <span className="text-xs text-slate-300" data-testid="public-ready-in-progress">Readiness in progress</span>}</div>
            </div>
            <h2 className="mt-10 mb-3 font-display text-lg font-bold text-slate-900">Verified documents</h2>
            <Docs docs={d.documents} />
            <p className="mt-8 flex items-start gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 shrink-0" />Requirements were researched for this business type and location, and the owner uploaded proof for each verified item to their GrantVerify vault. Document files are private and shared only on request. GrantVerify does not guarantee funding; lenders and sponsors should confirm documents directly with the owner.</p>
          </>
        )}
      </main>
      <DisclosureFooter />
    </div>
  );
}
